import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { FiCheck, FiClock, FiDownload, FiFileText, FiLoader, FiMic, FiPause, FiPhone, FiPhoneCall, FiPlay, FiX } from 'react-icons/fi';


const CallStatusIndicator = ({ callData, onCallComplete, onCallCancel }) => {
  const [status, setStatus] = useState('initiating'); // initiating, dialing, connected, completed, failed
  const [duration, setDuration] = useState(0);
  const [showOutcome, setShowOutcome] = useState(false);
  const [callOutcome, setCallOutcome] = useState('');

  // Simulate call progress
  useEffect(() => {
    if (!callData) return;

    const progressFlow = [
      { status: 'initiating', delay: 0 },
      { status: 'dialing', delay: 2000 },
      { status: 'connected', delay: 5000 },
      { status: 'completed', delay: 180000 } // 3 minutes
    ];

    progressFlow.forEach(({ status: newStatus, delay }) => {
      setTimeout(() => {
        setStatus(newStatus);
        if (newStatus === 'completed') {
          setShowOutcome(true);
        }
      }, delay);
    });

    // Duration counter
    const startTime = Date.now();
    const interval = setInterval(() => {
      if (status === 'connected') {
        setDuration(Math.floor((Date.now() - startTime - 5000) / 1000));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [callData, status]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusConfig = () => {
    const configs = {
      initiating: {
        icon: FiLoader,
        color: 'text-blue-600',
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'Initiating call...',
        description: 'Connecting to Thoughtly AI system'
      },
      dialing: {
        icon: FiPhone,
        color: 'text-orange-600',
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        text: 'Dialing...',
        description: `Calling ${callData?.phoneNumber}`
      },
      connected: {
        icon: FiPhoneCall,
        color: 'text-green-600',
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'Call in progress',
        description: `Connected • ${formatDuration(duration)}`
      },
      completed: {
        icon: FiCheck,
        color: 'text-green-600',
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'Call completed',
        description: `Duration: ${formatDuration(duration)}`
      },
      failed: {
        icon: FiX,
        color: 'text-red-600',
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'Call failed',
        description: 'Unable to complete the call'
      }
    };
    return configs[status] || configs.initiating;
  };

  const handleOutcomeSubmit = () => {
    if (!callOutcome) return;
    
    const callResult = {
      ...callData,
      status: 'completed',
      duration: formatDuration(duration),
      outcome: callOutcome,
      completedAt: new Date().toISOString(),
      transcript: generateMockTranscript(callOutcome),
      aiAnalysis: generateMockAnalysis(callOutcome)
    };

    onCallComplete(callResult);
    setShowOutcome(false);
  };

  const generateMockTranscript = (outcome) => {
    const transcripts = {
      interested: `AI: Hi Sarah, this is calling from Prosperity Leaders regarding career opportunities for experienced insurance professionals in your area.
Sarah: Oh, okay. What kind of opportunities?
AI: We work with top-performing agents who are looking to significantly increase their earning potential while maintaining their client relationships. Based on your background with State Farm, I think you'd be very interested in what we offer.
Sarah: I'm definitely open to learning more. I've been looking for ways to grow my business.
AI: That's great to hear! I'd love to have one of our regional managers give you a detailed overview of our compensation structure and support systems. Would you be available for a brief call this week?
Sarah: Yes, I think that would be helpful. When would work?
AI: Perfect! I'll have John Smith reach out to you within the next day to schedule something convenient. Thanks for your time, Sarah.
Sarah: Thank you, I look forward to hearing from him.`,
      
      callback_requested: `AI: Hi Sarah, this is calling from Prosperity Leaders regarding career opportunities for experienced insurance professionals.
Sarah: I'm actually in a meeting right now. Can you call me back later?
AI: Of course! When would be a better time to reach you?
Sarah: Maybe tomorrow afternoon around 2 PM would work better.
AI: Perfect, I'll make sure someone calls you tomorrow at 2 PM. Have a great day!
Sarah: Thank you.`,
      
      not_interested: `AI: Hi Sarah, this is calling from Prosperity Leaders regarding career opportunities for experienced insurance professionals.
Sarah: I'm not interested in changing companies right now. I'm happy where I am.
AI: I completely understand. If your situation ever changes in the future, would it be okay if we reached out again in about 6 months?
Sarah: I suppose that would be fine, but I'm really not looking to make any changes.
AI: No problem at all. Thanks for your time, and have a great day!
Sarah: Thank you.`
    };
    return transcripts[outcome] || transcripts.interested;
  };

  const generateMockAnalysis = (outcome) => {
    const analyses = {
      interested: {
        sentiment: 'Positive',
        confidence: 0.87,
        keyPoints: ['Expressed interest in growth opportunities', 'Mentioned looking for ways to expand business', 'Agreed to follow-up meeting'],
        nextSteps: ['Schedule overview meeting', 'Send company materials', 'Prepare compensation presentation'],
        recommendedStage: 'meeting_scheduled'
      },
      callback_requested: {
        sentiment: 'Neutral',
        confidence: 0.72,
        keyPoints: ['Was busy during call', 'Agreed to callback time', 'Polite and professional'],
        nextSteps: ['Schedule callback for tomorrow 2 PM', 'Send calendar invite', 'Prepare for follow-up'],
        recommendedStage: 'contacted'
      },
      not_interested: {
        sentiment: 'Negative',
        confidence: 0.91,
        keyPoints: ['Stated satisfaction with current role', 'Not looking to make changes', 'Agreed to future contact'],
        nextSteps: ['Add to 6-month follow-up list', 'Update notes', 'Set reminder for future contact'],
        recommendedStage: 'not_interested'
      }
    };
    return analyses[outcome] || analyses.interested;
  };

  if (!callData) return null;

  const config = getStatusConfig();

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className={`fixed bottom-6 right-6 ${config.bg} ${config.border} border rounded-xl p-4 shadow-lg max-w-sm z-40`}
      >
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 ${config.bg} rounded-lg flex items-center justify-center`}>
            <SafeIcon 
              icon={config.icon} 
              className={`${config.color} text-lg ${status === 'initiating' ? 'animate-spin' : ''}`} 
            />
          </div>
          
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-medium text-gray-900">{config.text}</h4>
              {status !== 'completed' && status !== 'failed' && (
                <button
                  onClick={onCallCancel}
                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                >
                  <SafeIcon icon={FiX} className="text-sm" />
                </button>
              )}
            </div>
            
            <p className="text-sm text-gray-600 mb-2">{config.description}</p>
            
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <SafeIcon icon={FiMic} />
              <span>{callData.agentName}</span>
              <span>•</span>
              <span>{callData.phoneNumber}</span>
            </div>

            {status === 'connected' && (
              <div className="mt-3 flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-green-600 font-medium">Live Call</span>
              </div>
            )}
          </div>
        </div>

        {/* Call Outcome Modal */}
        <AnimatePresence>
          {showOutcome && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="bg-white rounded-xl p-6 max-w-md w-full"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Call Outcome</h3>
                <p className="text-sm text-gray-600 mb-4">
                  How would you classify the result of this AI call?
                </p>
                
                <div className="space-y-2 mb-6">
                  {[
                    { value: 'interested', label: 'Interested', color: 'text-green-600' },
                    { value: 'callback_requested', label: 'Callback Requested', color: 'text-blue-600' },
                    { value: 'not_interested', label: 'Not Interested', color: 'text-red-600' },
                    { value: 'no_answer', label: 'No Answer', color: 'text-gray-600' },
                    { value: 'voicemail', label: 'Voicemail Left', color: 'text-yellow-600' }
                  ].map(outcome => (
                    <label key={outcome.value} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="outcome"
                        value={outcome.value}
                        checked={callOutcome === outcome.value}
                        onChange={(e) => setCallOutcome(e.target.value)}
                        className="text-purple-600 focus:ring-purple-500"
                      />
                      <span className={`font-medium ${outcome.color}`}>{outcome.label}</span>
                    </label>
                  ))}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowOutcome(false)}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleOutcomeSubmit}
                    disabled={!callOutcome}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                  >
                    Save Outcome
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};

export default CallStatusIndicator;